import { useState } from 'react'
import { Breadcrumbs } from '@/components/misc/Breadcrumbs'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select, SelectItem } from '@/components/ui/Select'
import { Plus } from 'lucide-react'
import { createProduct } from '@/services/mock/products'

export default function AddProductPage() {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('')
  const [discount, setDiscount] = useState('')
  const [taxIncluded, setTaxIncluded] = useState(true)
  const [stockQty, setStockQty] = useState('')
  const [stockStatus, setStockStatus] = useState('In Stock')
  const [featured, setFeatured] = useState(false)
  const [category, setCategory] = useState('')
  const [tag, setTag] = useState('')
  const [color, setColor] = useState('#000000')
  const [image, setImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  const handleImage = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImage(file)
    setImagePreview(URL.createObjectURL(file))
  }

  const handlePublish = async () => {
    try {
      const product = {
        name,
        description,
        price: parseFloat(price) || 0,
        discount: parseFloat(discount) || 0,
        taxIncluded,
        stockQuantity: stockQty || 'Unlimited',
        status: stockStatus,
        featured,
        categories: category ? [category] : [],
        tags: tag ? [tag] : [],
        colors: [color],
        images: imagePreview ? [imagePreview] : [],
      }
      await createProduct(product)
      alert('Product published (mock)')
    } catch (e) {
      console.error(e)
      alert('Failed to publish (mock)')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Add Product</h1>
          <Breadcrumbs />
        </div>

        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Input placeholder="Search product for add" />
          </div>
          <Button variant="primary" size="md" onClick={handlePublish}>Publish Product</Button>
          <Button variant="secondary" size="md">Save to draft</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left / Main form */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-card border rounded-xl p-6 space-y-6">
            <h3 className="text-lg font-medium">Basic Details</h3>
            <div>
              <label className="text-sm mb-1 block">Product Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div>
              <label className="text-sm mb-1 block">Product Description</label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>

          <div className="bg-card border rounded-xl p-6 space-y-6">
            <h3 className="text-lg font-medium">Pricing</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm mb-1 block">Product Price</label>
                <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="$999.89" />
              </div>
              <div>
                <label className="text-sm mb-1 block">Discounted Price (Optional)</label>
                <Input value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="$99" />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input type="radio" checked={taxIncluded} onChange={() => setTaxIncluded(true)} />
                <span className="text-sm">Tax Included</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" checked={!taxIncluded} onChange={() => setTaxIncluded(false)} />
                <span className="text-sm">No</span>
              </label>
            </div>
          </div>

          <div className="bg-card border rounded-xl p-6 space-y-6">
            <h3 className="text-lg font-medium">Inventory</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm mb-1 block">Stock Quantity</label>
                <Input value={stockQty} onChange={(e) => setStockQty(e.target.value)} placeholder="Unlimited" />
              </div>
              <div>
                <label className="text-sm mb-1 block">Stock Status</label>
                <Select value={stockStatus} onValueChange={(v) => setStockStatus(v)}>
                  <SelectItem value="In Stock">In Stock</SelectItem>
                  <SelectItem value="Out of Stock">Out of Stock</SelectItem>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />
                <span className="text-sm">Highlight this product in a featured section.</span>
              </label>
            </div>

            <div className="flex items-center gap-2 justify-end">
              <Button variant="secondary" size="md">Save to draft</Button>
              <Button variant="primary" size="md" onClick={handlePublish}>Publish Product</Button>
            </div>
          </div>
        </div>

        {/* Right / Sidebar */}
        <aside className="space-y-4">
          <div className="bg-card border rounded-xl p-4">
            <h4 className="text-sm font-medium">Upload Product Image</h4>
            <div className="mt-4">
              <div className="w-full h-40 border border-dashed rounded-lg flex items-center justify-center bg-background">
                {imagePreview ? (
                  <img src={imagePreview} alt="preview" className="h-full object-contain" />
                ) : (
                  <div className="text-muted-foreground">No image selected</div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <label className="inline-flex items-center px-3 py-2 bg-muted rounded cursor-pointer">
                  Browse
                  <input type="file" className="hidden" accept="image/*" onChange={handleImage} />
                </label>
                <Button variant="ghost" size="sm">Replace</Button>
              </div>
            </div>
          </div>

          <div className="bg-card border rounded-xl p-4 space-y-3">
            <h4 className="text-sm font-medium">Categories</h4>
            <div>
              <label className="text-sm mb-1 block">Product Categories</label>
              <Select value={category} onValueChange={(v) => setCategory(v)}>
                <SelectItem value="electronics">Electronics</SelectItem>
                <SelectItem value="fashion">Fashion</SelectItem>
                <SelectItem value="home">Home & Kitchen</SelectItem>
              </Select>
            </div>
            <div>
              <label className="text-sm mb-1 block">Product Tag</label>
              <Select value={tag} onValueChange={(v) => setTag(v)}>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="featured">Featured</SelectItem>
              </Select>
            </div>

            <div>
              <label className="text-sm mb-1 block">Select your color</label>
              <div className="flex items-center gap-2">
                {['#DDEFE7', '#F7E8E3', '#EDE7FA', '#F3F4F6', '#111827'].map((c) => (
                  <button key={c} onClick={() => setColor(c)} className={`w-8 h-8 rounded-full border ${color === c ? 'ring-2 ring-ring' : ''}`} style={{ background: c }} />
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}